import { useSyncExternalStore } from "react";

/** What a person chose: always light, always dark, or whatever the device says. */
export type ThemeChoice = "light" | "dark" | "system";

const KEY = "mashriqain.theme";
const EVENT = "mashriqain:theme";

function read(): ThemeChoice {
  try {
    const v = localStorage.getItem(KEY);
    return v === "light" || v === "dark" ? v : "system";
  } catch {
    return "system";
  }
}

function apply(choice: ThemeChoice) {
  const root = document.documentElement;
  if (choice === "system") root.removeAttribute("data-theme");
  else root.dataset.theme = choice;
}

/** Puts the stored choice on <html> before the first paint. Call once, before rendering. */
export function initTheme() {
  apply(read());
  // Another tab of the same panel changed it.
  window.addEventListener("storage", (e) => {
    if (e.key === KEY) {
      apply(read());
      window.dispatchEvent(new Event(EVENT));
    }
  });
}

export function setTheme(choice: ThemeChoice) {
  try {
    if (choice === "system") localStorage.removeItem(KEY);
    else localStorage.setItem(KEY, choice);
  } catch {
    // Private windows may refuse storage; the choice then lasts this page.
  }
  apply(choice);
  window.dispatchEvent(new Event(EVENT));
}

function subscribe(fn: () => void) {
  window.addEventListener(EVENT, fn);
  return () => window.removeEventListener(EVENT, fn);
}

export function useTheme(): [ThemeChoice, (c: ThemeChoice) => void] {
  const choice = useSyncExternalStore(subscribe, read, () => "system" as const);
  return [choice, setTheme];
}
